import { FC, useState } from 'react'
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  FloatingPortal
} from '@floating-ui/react-dom-interactions'

interface TooltipProviderProps {
  tooltip: string
  children: React.ReactNode
}

const TooltipProvider: FC<TooltipProviderProps> = ({ children, tooltip }) => {
  const [open, setOpen] = useState(false)

  const { x, y, reference, floating, strategy, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: 'top',
    whileElementsMounted: autoUpdate,
    middleware: [offset(5), flip(), shift()]
  })

  const hover = useHover(context, { move: false })
  const focus = useFocus(context)
  const dismiss = useDismiss(context)
  const role = useRole(context, { role: 'tooltip' })

  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role])
  return (
    <>
      <span ref={reference} {...getReferenceProps()}>
        {children}
      </span>
      <FloatingPortal>
        {open ? (
          <div
            ref={floating}
            style={{
              zIndex: 10000,
              position: strategy,
              top: y ?? 0,
              left: x ?? 0,
              width: 'max-content'
            }}
            {...getFloatingProps()}
          >
            <div className="tooltip-inner">{tooltip}</div>
          </div>
        ) : null}
      </FloatingPortal>
    </>
  )
}

export default TooltipProvider
